import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Folder, Download, Trash2 } from "lucide-react";
import { VIS, fmtBytes } from "./constants";

/**
 * The files-in-current-folder table (or search-across-all-folders when
 * `qActive` is set — extra "Folder" column with a chip that jumps back
 * into that folder's context).
 */
export function FilesTable({ docs, folderById, qActive,
                              onOpenFolder, onDownload, onDelete }) {
  return (
    <Card className="border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-xs">
          <tr>
            <th className="text-left px-3 py-2 text-zinc-400">Name</th>
            {qActive && <th className="text-left px-3 py-2 text-zinc-400">Folder</th>}
            <th className="text-left px-3 py-2 text-zinc-400">Type</th>
            <th className="text-left px-3 py-2 text-zinc-400">Visibility</th>
            <th className="text-left px-3 py-2 text-zinc-400">Uploaded</th>
            <th className="text-right px-3 py-2 text-zinc-400">Size</th>
            <th className="text-right px-3 py-2 text-zinc-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => {
            const vis = VIS.find((v) => v.value === d.visibility) || VIS[0];
            const homeFolder = d.folder_id ? folderById[d.folder_id] : null;
            return (
              <tr key={d.id} className="border-t border-white/5"
                  data-testid={`docs-file-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-zinc-100 truncate">{d.name}</div>
                      {d.description && <div className="text-[11px] text-zinc-500 truncate">{d.description}</div>}
                    </div>
                  </div>
                </td>
                {qActive && (
                  <td className="px-3 py-2 text-[11px] text-zinc-400">
                    {homeFolder ? (
                      <button onClick={() => onOpenFolder(homeFolder)}
                              className="inline-flex items-center gap-1 hover:text-orange-300"
                              data-testid={`docs-file-folder-${d.id}`}>
                        <Folder className="h-3 w-3" /> {homeFolder.name}
                      </button>
                    ) : <span className="text-zinc-500">Root</span>}
                  </td>
                )}
                <td className="px-3 py-2 text-[11px] text-zinc-500 truncate max-w-[100px]">
                  {(d.mime || "").split("/").pop()}
                </td>
                <td className="px-3 py-2"><Badge variant="outline" className={vis.tone}>{vis.label}</Badge></td>
                <td className="px-3 py-2 text-[11px] text-zinc-500">
                  {new Date(d.uploaded_at).toLocaleDateString()} · {d.uploaded_by_name}
                </td>
                <td className="px-3 py-2 text-right text-[11px] text-zinc-400 tabular-nums">
                  {fmtBytes(d.size)}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => onDownload(d)}
                            className="text-zinc-400 hover:text-emerald-300 p-1"
                            data-testid={`docs-download-${d.id}`}>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => onDelete(d)}
                            className="text-zinc-400 hover:text-red-300 p-1"
                            data-testid={`docs-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {docs.length === 0 && (
            <tr>
              <td colSpan={qActive ? 7 : 6}
                  className="px-3 py-8 text-center text-xs text-zinc-500"
                  data-testid="docs-empty">
                <FileText className="h-4 w-4 mx-auto mb-1 opacity-50" />
                {qActive
                  ? `No files match "${qActive}".`
                  : <>No files here. Click <b>Upload file</b> to add one.</>}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}
